import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  X, 
  Download, 
  ZoomIn, 
  ZoomOut, 
  RotateCw,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface AgreementDocument {
  id: string;
  title: string;
  documentUrl: string;
  pages: number;
  uploadedAt: string;
}

const AgreementDocumentViewer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [agreementDoc, setAgreementDoc] = useState<AgreementDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Mock document data - replace with actual API call
  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        // TODO: Replace with actual API call
        // const response = await fetchAgreementDocument(id);
        // setAgreementDoc(response.data);
        
        // Mock data for demonstration
        const mockDocument: AgreementDocument = {
          id: id || '1',
          title: 'College Partnership Agreement',
          documentUrl: '/api/agreements/document/sample-agreement.pdf', // Replace with actual document URL
          pages: 3,
          uploadedAt: '2024-01-15T10:30:00Z'
        };
        
        setTimeout(() => {
          setAgreementDoc(mockDocument);
          setLoading(false);
        }, 1000);
      } catch (err) {
        setError('Failed to load agreement document');
        setLoading(false);
      }
    };

    if (id) {
      loadDocument();
    }
  }, [id]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleDownload = () => {
    if (agreementDoc) {
      const link = window.document.createElement('a');
      link.href = agreementDoc.documentUrl;
      link.download = `${agreementDoc.title}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (agreementDoc && currentPage < agreementDoc.pages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleClose = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading agreement document...</p>
        </div>
      </div>
    );
  }

  if (error || !agreementDoc) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <div className="text-white text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-2">Document Not Found</h2>
          <p className="text-gray-300 mb-6">{error || 'The requested agreement document could not be found.'}</p>
          <button
            onClick={handleClose}
            className="px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 bg-black z-50 ${isFullscreen ? '' : 'p-4'}`}>
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-black bg-opacity-90 text-white p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold">{agreementDoc.title}</h1>
              <p className="text-sm text-gray-300">
                Page {currentPage} of {agreementDoc.pages} • Uploaded {new Date(agreementDoc.uploadedAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Page Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm px-3 py-1 bg-gray-700 rounded">
                {currentPage} / {agreementDoc.pages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage >= agreementDoc.pages}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Zoom Controls */}
            <div className="flex items-center gap-2 border-l border-gray-600 pl-4">
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <span className="text-sm px-3 py-1 bg-gray-700 rounded min-w-[60px] text-center">
                {zoom}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
            </div>

            {/* Other Controls */}
            <div className="flex items-center gap-2 border-l border-gray-600 pl-4">
              <button
                onClick={handleRotate}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                title="Rotate"
              >
                <RotateCw className="h-5 w-5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
              </button>
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer */}
      <div className="absolute inset-0 pt-20 pb-20 px-4">
        <div className="h-full flex items-center justify-center">
          <div 
            className="bg-white rounded-lg shadow-2xl overflow-hidden max-w-4xl w-full"
            style={{
              transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
              transformOrigin: 'center',
              transition: 'transform 0.3s ease',
              maxHeight: '100%'
            }}
          >
            {/* Document Content */}
            <div className="w-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)' }}>
              <div className="w-full bg-white p-8">
                <div className="max-w-3xl mx-auto">
                  {/* Page 1 */}
                  {currentPage === 1 && (
                    <div>
                      {/* Agreement Header */}
                      <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">SERVICE AGREEMENT</h1>
                        <p className="text-lg text-gray-600">Training & Placement Services</p>
                        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="text-sm text-orange-800">
                            <strong>Effective Date:</strong> September 11, 2025<br/>
                            <strong>Term:</strong> One (1) Year<br/>
                            <strong>Renewal:</strong> Upon mutual agreement
                          </p>
                        </div>
                      </div>

                      {/* Parties */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">PARTIES TO THIS AGREEMENT</h2>
                        <p className="text-sm text-gray-700 mb-4">
                          This Service Agreement ("Agreement") is made and entered into on this 11th day of September 2025, by and between:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="p-4 border border-gray-200 rounded-lg">
                            <h3 className="font-semibold text-gray-900 mb-2">Victa EarlyJobs Technologies Pvt. Ltd.</h3>
                            <p className="text-sm text-gray-600">
                              Having its registered office at No 53, Hustlehub, 5th cross, 4th Block, Koramangala, Bangalore-560034<br/>
                              hereinafter referred to as "EarlyJobs", which expression shall, unless it be repugnant to the subject or context thereof, include its successors and assigns
                            </p>
                          </div>
                          <div className="p-4 border border-gray-200 rounded-lg">
                            <h3 className="font-semibold text-gray-900 mb-2">[University/College Name]</h3>
                            <p className="text-sm text-gray-600">
                              Having its principal place of business at [Address]<br/>
                              hereinafter referred to as "Institution", which expression shall, unless it be repugnant to the subject or context thereof, include its successors and assigns.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Objective */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">1. OBJECTIVE OF THE AGREEMENT</h2>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          This Agreement sets forth the terms under which EarlyJobs and the Institution agree to collaborate for the purpose of enhancing placement opportunities, skill development and job readiness of students enrolled in the Institution.
                        </p>
                      </div>

                      {/* Scope of Services */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">2. SCOPE OF SERVICES</h2>
                        <p className="text-sm text-gray-700 mb-4">EarlyJobs agrees to provide the following services to the Institution and its students:</p>
                        
                        <div className="space-y-4">
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.1 Placement Support</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Access to over 3500+ active monthly job and internship openings</li>
                              <li>• Direct recruiter onboarding and assistance in running campus placement drives</li>
                              <li>• Coordination of college-specific placement drives, job fairs and hiring events</li>
                            </ul>
                          </div>

                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.2 AI-Based Assessment Platform</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Access to the EarlyJobs AI Assessment Tool for job role-based skill evaluation</li>
                              <li>• Customized assessments for technical, non-technical and domain-specific roles</li>
                              <li>• Dashboards to track student performance and skill readiness</li>
                            </ul>
                          </div>

                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.3 Internship in Recruitment (Remote)</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Option for selected students to participate in part-time or full-time recruitment internships</li>
                              <li>• Certification upon successful completion of the internship</li>
                            </ul>
                          </div>

                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.4 Resume & Interview Training</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Resume building workshops</li>
                              <li>• Mock interviews and personalized feedback sessions by industry experts</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Page 2 */}
                  {currentPage === 2 && (
                    <div>
                      <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">SERVICE AGREEMENT</h1>
                        <p className="text-lg text-gray-600">Training & Placement Services - Page 2</p>
                      </div>

                      {/* Continued Scope of Services */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">2. SCOPE OF SERVICES (CONTINUED)</h2>
                        
                        <div className="space-y-4">
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.5 Campus Ambassador Initiative</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Institutions can nominate student ambassadors to promote opportunities</li>
                              <li>• Ambassadors will receive certification and performance-based incentives</li>
                            </ul>
                          </div>

                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.6 Skill Development & Career Readiness</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Career guidance webinars, masterclasses and soft skills sessions</li>
                              <li>• AI-driven career-matching for job roles based on candidate profile</li>
                            </ul>
                          </div>

                          <div>
                            <h3 className="font-semibold text-gray-900 mb-2">2.7 Recruiter and Placement Reports</h3>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4">
                              <li>• Analytics reports for recruiters on student assessment and readiness</li>
                              <li>• Detailed placement reports to the Institution's Placement Cell</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Fee Clause */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">3. FEE CLAUSE</h2>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>3.1</strong> There shall be no upfront charges or subscription fee for the Institution or its students to access core services offered by EarlyJobs. The Institution, if approached by any EarlyJobs Franchisee, shall ensure that no payments are made to the Franchisee for services provided under this Agreement.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>3.2</strong> EarlyJobs will charge a nominal success-based placement fee from hiring companies and not from the Institution or students.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>3.3</strong> In case of paid value-added services such as AI-Based Skill Assessments, training bootcamps, or workshops these shall be chargeable as per standard pricing and clearly mentioned on the EarlyJobs platform.
                            </p>
                            <ul className="text-sm text-gray-700 space-y-1 ml-4 mt-2">
                              <li>• These payments shall be made only via the official EarlyJobs portal</li>
                              <li>• No Franchisee or third-party representative is authorized to collect payment for these services</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Exclusivity Clause */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">4. EXCLUSIVITY CLAUSE</h2>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>4.1</strong> The Institution agrees to work exclusively with EarlyJobs for AI-based assessments and placement-related services for the term of this Agreement.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>4.2</strong> This exclusivity ensures focused support, joint branding and committed resource allocation from EarlyJobs.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>4.3</strong> Any exceptions to this exclusivity must be communicated and agreed to in writing by both parties.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Joint Certification Clause */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">5. JOINT CERTIFICATION CLAUSE</h2>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>5.1</strong> Students who complete internships, AI assessments, or placement training through EarlyJobs shall receive Joint Certifications bearing the logos and names of both EarlyJobs and the Institution.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>5.2</strong> These certifications will be digitally issued and may be used for resumes, LinkedIn and job applications.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>5.3</strong> EarlyJobs and the Institution will jointly organize certification ceremonies, if required.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Page 3 */}
                  {currentPage === 3 && (
                    <div>
                      <div className="text-center mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">SERVICE AGREEMENT</h1>
                        <p className="text-lg text-gray-600">Training & Placement Services - Page 3</p>
                      </div>

                      {/* Responsibilities of the Institution */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">6. RESPONSIBILITIES OF THE INSTITUTION</h2>
                        <ul className="text-sm text-gray-700 space-y-2 ml-4">
                          <li>• Assign a Placement Cell SPOC for coordination</li>
                          <li>• Promote EarlyJobs' student registration portal internally</li>
                          <li>• Facilitate scheduling of AI assessments, resume workshops and mock interviews</li>
                          <li>• Provide access to college infrastructure (e.g., seminar halls or computer labs) for sessions, if conducted offline</li>
                        </ul>
                      </div>

                      {/* Term and Termination */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">7. TERM AND TERMINATION</h2>
                        <div className="space-y-4">
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>7.1</strong> This Agreement shall remain in effect for a term of one (1) year from the date of signing and may be renewed upon mutual agreement.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>7.2</strong> Either party may terminate this Agreement by providing 30 days' prior written notice.
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              <strong>7.3</strong> In case of breach or non-performance, the non-defaulting party may terminate the agreement with immediate effect.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Confidentiality */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">8. CONFIDENTIALITY</h2>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Each party agrees to keep all student, institutional and proprietary data confidential and not disclose it to any third party without prior written consent.
                        </p>
                      </div>

                      {/* Dispute Resolution */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">9. DISPUTE RESOLUTION</h2>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          All disputes arising out of this Agreement shall be settled amicably between both parties. In the event of failure to resolve, the matter shall be referred to arbitration in accordance with the Arbitration and Conciliation Act, 1996. The venue for arbitration shall be [City], India.
                        </p>
                      </div>

                      {/* Jurisdiction */}
                      <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">10. JURISDICTION</h2>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          This Agreement shall be governed in accordance with the laws of India. The courts of [City] shall have exclusive jurisdiction.
                        </p>
                      </div>

                      {/* Signatures */}
                      <div className="mt-12">
                        <div className="mb-6">
                          <p className="text-sm text-gray-700 text-center">
                            IN WITNESS WHEREOF, the parties hereto have executed this Agreement as of the day and year first above written.
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div className="text-center">
                            <div className="border-b border-gray-300 pb-2 mb-4">
                              <p className="text-sm font-semibold">For Victa EarlyJobs Technologies Pvt. Ltd.</p>
                            </div>
                            <div className="h-16 border-b border-gray-300 mb-2"></div>
                            <p className="text-xs text-gray-600">Authorized Signatory</p>
                            <p className="text-xs text-gray-600">Name: _________________________</p>
                            <p className="text-xs text-gray-600">Designation: ____________________</p>
                            <p className="text-xs text-gray-600">Date: _________________________</p>
                          </div>
                          <div className="text-center">
                            <div className="border-b border-gray-300 pb-2 mb-4">
                              <p className="text-sm font-semibold">For [College/University Name]</p>
                            </div>
                            <div className="h-16 border-b border-gray-300 mb-2"></div>
                            <p className="text-xs text-gray-600">Authorized Signatory</p>
                            <p className="text-xs text-gray-600">Name: _________________________</p>
                            <p className="text-xs text-gray-600">Designation: ____________________</p>
                            <p className="text-xs text-gray-600">Date: _________________________</p>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-8 text-center text-xs text-gray-500 border-t pt-4">
                        <p>This agreement is governed by the laws of India. Any disputes shall be subject to the jurisdiction of courts in [City].</p>
                        <p className="mt-2">Document Version: 1.0 | Last Updated: September 11, 2025</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-90 text-white p-4 z-10">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-300">
            Use mouse wheel to zoom • Drag to pan • Click to focus
          </div>
          <div className="text-sm text-gray-300">
            Agreement Document Viewer
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementDocumentViewer;
